import { Box, Text } from "@saleor/macaw-ui-next";
import React from "react";

import { CustomerAnalyticsData } from "../../types";

interface CustomerAnalyticsProps {
  data: CustomerAnalyticsData;
  currency: string;
}

export const CustomerAnalytics: React.FC<CustomerAnalyticsProps> = ({ data, currency }) => {
  const formatCurrency = (val: number) =>
    `${currency} ${Number(val || 0).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  const cards = [
    { label: "Total Ordering Customers", value: data.totalCustomers.toLocaleString() },
    { label: "New Customers", value: data.newCustomersCount.toLocaleString() },
    { label: "Returning Customers", value: data.returningCustomersCount.toLocaleString() },
    { label: "Retention Rate", value: `${data.retentionRatePercentage}%` },
    {
      label: "Avg Customer Lifetime Value",
      value: formatCurrency(data.averageCustomerLifetimeValue),
    },
    { label: "Purchase Frequency", value: `${data.purchaseFrequency} orders/cust` },
  ];

  return (
    <Box display="flex" flexDirection="column" gap={6}>
      {/* Metric Cards Grid */}
      <Box display="grid" __gridTemplateColumns="repeat(auto-fit, minmax(220px, 1fr))" gap={4}>
        {cards.map((card, idx) => (
          <Box
            key={idx}
            padding={4}
            borderWidth={1}
            borderStyle="solid"
            borderColor="default1"
            borderRadius={3}
            backgroundColor="default1"
            display="flex"
            flexDirection="column"
            gap={1}
          >
            <Text size={2} color="default1" fontWeight="bold">
              {card.label}
            </Text>
            <Text size={6} fontWeight="bold">
              {card.value}
            </Text>
          </Box>
        ))}
      </Box>

      {/* Top Customers Table */}
      <Box
        borderWidth={1}
        borderStyle="solid"
        borderColor="default1"
        borderRadius={3}
        backgroundColor="default1"
        padding={4}
        display="flex"
        flexDirection="column"
        gap={3}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Text size={4} fontWeight="bold">
            Top Customers Ranked by Spend
          </Text>
          <Text size={2} color="default1">
            {data.topCustomers.length} Customers
          </Text>
        </Box>

        <div style={{ overflowX: "auto" }}>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              textAlign: "left",
              fontSize: "13px",
            }}
          >
            <thead>
              <tr style={{ borderBottom: "1px solid #eee", backgroundColor: "#fafafa" }}>
                <th style={{ padding: "10px" }}>Customer</th>
                <th style={{ padding: "10px" }}>Phone</th>
                <th style={{ padding: "10px" }}>Email</th>
                <th style={{ padding: "10px", textAlign: "center" }}>Orders Placed</th>
                <th style={{ padding: "10px", textAlign: "right" }}>Total Spend</th>
                <th style={{ padding: "10px", textAlign: "right" }}>AOV</th>
                <th style={{ padding: "10px", textAlign: "center" }}>Last Order Date</th>
              </tr>
            </thead>
            <tbody>
              {data.topCustomers.length === 0 ? (
                <tr>
                  <td colSpan={7} style={{ padding: "20px", textAlign: "center", color: "#888" }}>
                    No customer data recorded for this period.
                  </td>
                </tr>
              ) : (
                data.topCustomers.map((c, idx) => (
                  <tr key={idx} style={{ borderBottom: "1px solid #f0f0f0" }}>
                    <td style={{ padding: "10px", fontWeight: "bold" }}>{c.name}</td>
                    <td style={{ padding: "10px", color: "#555" }}>{c.phone || "—"}</td>
                    <td style={{ padding: "10px", color: "#555" }}>{c.email || "—"}</td>
                    <td style={{ padding: "10px", textAlign: "center", fontWeight: "bold" }}>
                      {c.ordersCount}
                    </td>
                    <td
                      style={{
                        padding: "10px",
                        textAlign: "right",
                        color: "#2e7d32",
                        fontWeight: "bold",
                      }}
                    >
                      {formatCurrency(c.totalSpend)}
                    </td>
                    <td style={{ padding: "10px", textAlign: "right" }}>
                      {formatCurrency(c.averageOrderValue)}
                    </td>
                    <td style={{ padding: "10px", textAlign: "center", color: "#666" }}>
                      {c.lastOrderDate || "—"}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Box>
    </Box>
  );
};
